import type { IngredientListComponent } from '../allergen-core/types';
import type { AdditiveFunction } from '../dto/ingredient/additive-function.enum';
import type { BreakdownExemption } from '../dto/ingredient/breakdown-exemption.enum';
import { ADDITIVE_FUNCTION_LABELS, type IngredientListLocale } from './additive-function-labels';
import {
  type AllergenEmphasisTarget,
  appendMissingAllergenEvidence,
  findAllergenEmphasisRanges,
  type TextEmphasisRange,
} from './allergen-emphasis';

/**
 * Composizione pura del TESTO dell'elenco ingredienti (art. 18 e Allegato VII
 * Reg. UE 1169/2011, CELEX 32011R1169): designazione della riga, parentetica
 * dei componenti con parentesi alternate per livello (Parte E), additivi come
 * "categoria funzionale: nome o numero E" (Parte C), percentuale dichiarata
 * dal fornitore. Unica regola per cartellino, registro, preimballato e
 * anteprima del frontend: non ordina righe, non valuta esenzioni
 * dell'Allegato II (le decide il chiamante passando i `containsTargets`).
 *
 * `declaredPercent` e' la percentuale stampata sull'etichetta del semilavorato
 * acquistato, riportata tale e quale: non e' mai il QUID del prodotto finito
 * (art. 22 e Allegato VIII), che si calcola altrove.
 */

export interface IngredientListTextRow {
  name: string;
  /** Art. 18 §3: `(nano)` dopo la designazione. */
  isEngineeredNanomaterial?: boolean;
  additiveFunction?: AdditiveFunction | null;
  additiveENumber?: string | null;
  /** Presente => nessuna parentetica dei componenti. */
  breakdownExemption?: BreakdownExemption | null;
  components?: readonly IngredientListComponent[];
}

export interface IngredientListTextLine {
  text: string;
  emphasis: TextEmphasisRange[];
}

type DesignationOwner = 'ingredient' | 'component';

const PERCENT_LOCALE_TAG: Readonly<Record<IngredientListLocale, string>> = {
  it: 'it-IT',
  en: 'en-GB',
};

/** "53%", "53,5%" (virgola decimale in italiano), senza zeri finali ne' separatori delle migliaia. */
export function formatDeclaredPercent(value: number, locale: IngredientListLocale): string {
  const formatted = new Intl.NumberFormat(PERCENT_LOCALE_TAG[locale], {
    maximumFractionDigits: 2,
    useGrouping: false,
  }).format(value);
  return `${formatted}%`;
}

/**
 * "categoria funzionale: nome o numero E". Una categoria nuda non e' una
 * dichiarazione conforme (art. 20 lett. a, Allegato VII Parte C): il gate di
 * conferma la blocca prima, questo throw e' la seconda linea di difesa — un
 * fatto legale non deve sparire dietro un fallback "ragionevole".
 */
export function composeAdditiveDesignation(
  item: {
    name: string;
    additiveFunction?: AdditiveFunction | null;
    additiveENumber?: string | null;
  },
  locale: IngredientListLocale,
  owner: DesignationOwner = 'component',
): string {
  if (!item.additiveFunction) {
    return item.name;
  }
  const categoryLabel = ADDITIVE_FUNCTION_LABELS[locale][item.additiveFunction];
  if (item.additiveFunction === 'MODIFIED_STARCH') {
    return categoryLabel;
  }
  const specificName = item.name.trim() || item.additiveENumber?.trim();
  if (!specificName) {
    throw new Error(
      `ingredient-list.service: additive ${owner} with function "${item.additiveFunction}" has neither a name nor an E-number — a bare functional category is not a conforming ingredient declaration (art. 20 lett. a Reg. UE 1169/2011); the confirmation gate must reject this before it reaches label rendering.`,
    );
  }
  return `${categoryLabel}: ${specificName}`;
}

/**
 * Allegato VII Parte E: l'elenco di un composto annidato alterna le
 * parentesi per livello. Le tonde le aggiunge la riga; i componenti di
 * profondita' 1 aprono le quadre, quelli di profondita' 2 tornano alle tonde,
 * e cosi' via. Sotto-voci inline e sottoalberi collegati seguono la stessa
 * regola; un carry-over esente (art. 20 lett. b) sparisce con le sue figlie.
 */
export function composeComponentListText(
  components: readonly IngredientListComponent[],
  depth: number,
  locale: IngredientListLocale,
): string | null {
  const componentTexts = [...components]
    .filter((component) => !component.isCarryOverExempt)
    .sort((left, right) => left.sortOrder - right.sortOrder)
    .map((component) => composeNestedComponentText(component, depth, locale));
  return componentTexts.length > 0 ? componentTexts.join(', ') : null;
}

function composeNestedComponentText(
  component: IngredientListComponent,
  depth: number,
  locale: IngredientListLocale,
): string {
  const designation = withDeclaredPercent(
    composeAdditiveDesignation(component, locale),
    component.declaredPercent,
    locale,
  );
  if (component.breakdownExempt || !component.children) {
    return designation;
  }
  const childrenText = composeComponentListText(component.children, depth + 1, locale);
  if (!childrenText) {
    return designation;
  }
  return depth % 2 === 1 ? `${designation} [${childrenText}]` : `${designation} (${childrenText})`;
}

function withDeclaredPercent(
  designation: string,
  declaredPercent: number | undefined,
  locale: IngredientListLocale,
): string {
  return declaredPercent === undefined
    ? designation
    : `${designation} ${formatDeclaredPercent(declaredPercent, locale)}`;
}

/** Testo della riga senza evidenza allergeni: designazione, `(nano)`, parentetica dei componenti. */
export function composeIngredientLineText(
  row: IngredientListTextRow,
  locale: IngredientListLocale,
): string {
  const designation = composeAdditiveDesignation(row, locale, 'ingredient');
  const ingredientText = row.isEngineeredNanomaterial ? `${designation} (nano)` : designation;
  const breakdownText =
    row.breakdownExemption || !row.components
      ? null
      : composeComponentListText(row.components, 1, locale);
  return breakdownText ? `${ingredientText} (${breakdownText})` : ingredientText;
}

/**
 * Riga completa: testo, "(sostanza)" appesa per ogni CONTAINS non gia'
 * leggibile (art. 21 §1 lett. b) e intervalli da evidenziare calcolati UNA
 * volta sul testo definitivo. `containsTargets` arriva gia' filtrato dalle
 * esenzioni dell'Allegato II: un MAY_CONTAIN non entra mai nell'elenco.
 */
export function composeIngredientListLine(
  row: IngredientListTextRow,
  containsTargets: readonly AllergenEmphasisTarget[],
  locale: IngredientListLocale,
): IngredientListTextLine {
  const text = appendMissingAllergenEvidence(
    composeIngredientLineText(row, locale),
    containsTargets,
  );
  return { text, emphasis: findAllergenEmphasisRanges(text, containsTargets) };
}
